import { useState } from 'react';
import { isInTelegram, alertDialog } from '../telegram';
import { createDepositInvoice, sendDepositInvoiceLink, downloadDepositInvoice } from '../api';
import type { DepositAccount, DepositInvoice } from '../types';

// ============================================================================
// Блок B (депозиты): окно «Пополнить депозит».
// Шаг 1 — выбор кофейни (если их несколько) и суммы → выставляем счёт ДП-….
// Шаг 2 — счёт выставлен: назначение платежа и способы получить PDF. В Telegram
// и на e-mail уходит ТОЛЬКО ссылка на скачивание (файлы не шлём), в обычном
// браузере можно скачать PDF напрямую.
// ============================================================================

const fmtPrice = (n: number) =>
  n.toLocaleString('ru-RU', { style: 'currency', currency: 'RUB', minimumFractionDigits: 2 });

const btnPrimary: React.CSSProperties = {
  width: '100%', padding: '12px', border: 'none', borderRadius: 12,
  background: 'var(--tg-button)', color: 'var(--tg-button-text, #fff)',
  fontSize: 15, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer',
};

const btnSecondary: React.CSSProperties = {
  ...btnPrimary,
  background: 'var(--tg-secondary-bg)',
  color: 'var(--tg-button)',
};

interface Props {
  shops: DepositAccount[];
  initialShopId?: string | null;
  onClose: () => void;
  // Вызывается после успешного выставления счёта (родитель перечитывает депозиты).
  onIssued?: () => void;
}

export default function TopUpModal({ shops, initialShopId, onClose, onIssued }: Props) {
  const [shopId, setShopId] = useState<string>(initialShopId ?? shops[0]?.coffee_shop_id ?? '');
  const shop = shops.find(s => s.coffee_shop_id === shopId) ?? shops[0];
  const [amount, setAmount] = useState<string>(
    shop ? String(Math.max(1, Math.round(Number(shop.suggested_topup)))) : '3000',
  );
  const [loading, setLoading] = useState(false);
  const [invoice, setInvoice] = useState<DepositInvoice | null>(null);
  const [sending, setSending] = useState<string | null>(null);

  const minOrder = shop ? Number(shop.min_order_amount) : 0;
  const available = shop ? Number(shop.available) : 0;
  const amountNum = Number(amount.replace(',', '.'));
  const amountValid = Number.isFinite(amountNum) && amountNum >= 1 && amountNum <= 1_000_000;
  const afterTopup = available + (amountValid ? amountNum : 0);
  const warnLow = amountValid && afterTopup < minOrder;

  const changeShop = (id: string) => {
    setShopId(id);
    const s = shops.find(x => x.coffee_shop_id === id);
    if (s) setAmount(String(Math.max(1, Math.round(Number(s.suggested_topup)))));
  };

  const submit = async () => {
    if (!shop || !amountValid || loading) return;
    setLoading(true);
    try {
      const { data } = await createDepositInvoice(shop.coffee_shop_id, amountNum);
      setInvoice(data);
      onIssued?.();
    } catch (e) {
      alertDialog(`Не удалось выставить счёт: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  };

  const send = async (channel: 'telegram' | 'email') => {
    if (!invoice || sending) return;
    setSending(channel);
    try {
      await sendDepositInvoiceLink(invoice.id, channel);
      alertDialog(channel === 'telegram'
        ? 'Ссылка на счёт отправлена в чат с ботом.'
        : 'Ссылка на счёт отправлена на ваш e-mail.');
    } catch (e) {
      alertDialog((e as Error).message);
    } finally {
      setSending(null);
    }
  };

  const download = async () => {
    if (!invoice || sending) return;
    setSending('download');
    try {
      await downloadDepositInvoice(invoice.id, invoice.number);
    } catch (e) {
      alertDialog((e as Error).message);
    } finally {
      setSending(null);
    }
  };

  const copyPurpose = async () => {
    if (!invoice?.purpose_text) return;
    try {
      await navigator.clipboard.writeText(invoice.purpose_text);
      alertDialog('Назначение платежа скопировано.');
    } catch {
      alertDialog('Скопируйте назначение платежа вручную.');
    }
  };

  return (
    <div className="product-modal-overlay" onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 420, maxHeight: '90vh', overflowY: 'auto',
          background: 'var(--tg-bg)', color: 'var(--tg-text)', borderRadius: 16, padding: 20,
        }}
      >
        {!invoice ? (
          <>
            <h3 style={{ margin: '0 0 12px', fontSize: 18 }}>Пополнить депозит</h3>

            {shops.length > 1 && (
              <label style={{ display: 'block', marginBottom: 12 }}>
                <span className="hint">Кофейня</span>
                <select
                  value={shopId}
                  onChange={e => changeShop(e.target.value)}
                  style={{ width: '100%', marginTop: 4, padding: 10, borderRadius: 10, fontFamily: 'inherit', fontSize: 15 }}
                >
                  {shops.map(s => (
                    <option key={s.coffee_shop_id} value={s.coffee_shop_id}>{s.coffee_shop_name}</option>
                  ))}
                </select>
              </label>
            )}

            {shop && (
              <div style={{ background: 'var(--tg-secondary-bg)', borderRadius: 12, padding: '10px 12px', marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span className="hint">{shop.coffee_shop_name}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                  <span>Доступно сейчас</span>
                  <b>{fmtPrice(available)}</b>
                </div>
                {Number(shop.reserved) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
                    <span className="hint">в резерве под заказы</span>
                    <span className="hint">{fmtPrice(Number(shop.reserved))}</span>
                  </div>
                )}
              </div>
            )}

            <label style={{ display: 'block', marginBottom: 6 }}>
              <span className="hint">Сумма пополнения, ₽</span>
              <input
                type="number"
                inputMode="decimal"
                min={1}
                step={100}
                value={amount}
                onChange={e => setAmount(e.target.value)}
                style={{
                  width: '100%', marginTop: 4, padding: 12, borderRadius: 10, fontSize: 18, fontWeight: 700,
                  border: '1px solid var(--tg-secondary-bg)', fontFamily: 'inherit', boxSizing: 'border-box',
                }}
              />
            </label>
            <p className="hint" style={{ margin: '0 0 10px' }}>
              Минимальный заказ {minOrder.toLocaleString('ru-RU')} ₽. Пополняйте с запасом:
              на заказ {(minOrder + 11).toLocaleString('ru-RU')} ₽ депозита в {minOrder.toLocaleString('ru-RU')} ₽ не хватит.
            </p>
            {warnLow && (
              <p style={{ margin: '0 0 10px', color: '#ff3b30', fontSize: 13, fontWeight: 600 }}>
                После пополнения доступно будет {fmtPrice(afterTopup)} — меньше минимального заказа.
                Заказ оформить не получится, пока остаток ниже {fmtPrice(minOrder)}.
              </p>
            )}

            <button onClick={submit} disabled={!amountValid || loading || !shop} style={{ ...btnPrimary, opacity: !amountValid || loading ? 0.6 : 1 }}>
              {loading ? 'Выставляем счёт…' : `Выставить счёт на ${amountValid ? fmtPrice(amountNum) : '…'}`}
            </button>
            <button onClick={onClose} disabled={loading} style={{ ...btnSecondary, marginTop: 8 }}>
              Отмена
            </button>
          </>
        ) : (
          <>
            <h3 style={{ margin: '0 0 6px', fontSize: 18 }}>Счёт выставлен</h3>
            <p style={{ margin: '0 0 12px', fontSize: 16 }}>
              <b>{invoice.number}</b> на <b>{fmtPrice(Number(invoice.amount))}</b>
            </p>

            <div style={{ background: 'var(--tg-secondary-bg)', borderRadius: 12, padding: '10px 12px', marginBottom: 12 }}>
              <div className="hint" style={{ marginBottom: 4 }}>Назначение платежа (укажите в платёжке):</div>
              <div style={{ fontSize: 14, lineHeight: 1.35 }}>{invoice.purpose_text}</div>
              <div className="hint" style={{ marginTop: 6 }}>Оплата только с расчётного счёта вашей организации или ИП. Платежи с личных карт не зачисляются.</div>
              <button onClick={copyPurpose} style={{ ...btnSecondary, marginTop: 8, padding: '8px', fontSize: 14, background: 'var(--tg-bg)' }}>
                Скопировать назначение
              </button>
            </div>

            {isInTelegram() ? (
              <button onClick={() => send('telegram')} disabled={!!sending} style={{ ...btnPrimary, opacity: sending ? 0.6 : 1 }}>
                {sending === 'telegram' ? 'Отправляем…' : '📩 Ссылку на счёт в чат'}
              </button>
            ) : (
              <button onClick={download} disabled={!!sending} style={{ ...btnPrimary, opacity: sending ? 0.6 : 1 }}>
                {sending === 'download' ? 'Загрузка…' : '📄 Скачать PDF'}
              </button>
            )}
            <button onClick={() => send('email')} disabled={!!sending} style={{ ...btnSecondary, marginTop: 8, opacity: sending ? 0.6 : 1 }}>
              {sending === 'email' ? 'Отправляем…' : '✉️ Ссылку на счёт на e-mail'}
            </button>

            <p className="hint" style={{ margin: '12px 0 0' }}>
              Зачисление на депозит происходит автоматически после поступления денег в банк,
              обычно в течение 10 минут. Мы сообщим в Telegram и по почте.
            </p>

            <button onClick={onClose} style={{ ...btnSecondary, marginTop: 12 }}>
              Готово
            </button>
          </>
        )}
      </div>
    </div>
  );
}
